import { Col, DatePicker, Form, Input, Row, notification } from 'antd';
import { localize, filterDigits } from 'mixins/helpers';
import Button from 'mixins/ui/button/button';
import PartWrapper from 'mixins/ui/part-wrapper';
import React, { useEffect, useRef, useState } from 'react';
import 'react-app-polyfill/stable';
import { getDocument } from './actions';
import './find-document-confirming-residence.scss';
import Recaptcha from '../../mixins/ui/recaptcha/recaptcha';
import PrintForm2 from 'mixins/ui/print-form-2';
import dayjs, { Dayjs } from 'dayjs';

function getURLParameters(url: string) {
    const params = {};

    if (url) {
        const queryString = url.split('?')[1];

        if (queryString) {
            const paramPairs = queryString.split('&');

            paramPairs.forEach(function (pair) {
                const [key, value] = pair.split('=').map(decodeURIComponent);
                params[key] = value;
            });
        }
    }
    return params;
}

interface FormValues {
    iinBin: string;
    regNumber: string;
    issueDate: Dayjs;
}

type RequestValues = Omit<FormValues, 'issueDate'> & { issueDate: string };

const toRequest = (values: FormValues): RequestValues => ({
    ...values,
    issueDate: values.issueDate.format('YYYY-MM-DD'),
});

const toForm = (values: RequestValues): FormValues => ({
    ...values,
    issueDate: dayjs(values.issueDate),
});

const isValidRequest = (values: RequestValues) =>
    values.iinBin &&
    values.issueDate &&
    values.iinBin.length === 12 &&
    Number.isFinite(Number(values.iinBin)) &&
    dayjs(values.issueDate).isValid();

const FindDocumentConfirmingResidence = (props) => {
    const { iinBin, regNumber, issueDate } = props.config;

    const [pdfDocument, setPdfDocument] = useState<ArrayBuffer | null>(null);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm<FormValues>();
    const showOnScan = useRef<boolean>();
    const captchaRef = useRef(null);

    useEffect(() => {
        const params = getURLParameters(window.location.href) as RequestValues;

        if (Object.keys(params).length) {
            if (isValidRequest(params)) {
                setLoading(true);
                getDocument(params)
                    .then((res) => {
                        form.setFieldsValue(toForm(params));
                        setPdfDocument(res.data);
                        showOnScan.current = true;
                        setTimeout(() => (showOnScan.current = false));
                    })
                    .catch(() => setPdfDocument(null))
                    .finally(() => setLoading(false));
            } else {
                notification.error({
                    message: localize('error', props.commonTranslations),
                    description: localize(
                        'error.residence-document-not-found',
                        props.commonTranslations
                    ),
                });
            }
        }
    }, []);

    const clearForm = () => {
        form.resetFields();
        setPdfDocument(null);
    };

    const onFinish = async (values: FormValues) => {
        const recaptcha = captchaRef.current.getValue();

        if (recaptcha.verified) {
            setLoading(true);

            getDocument(toRequest(values))
                .then(({ data }) => {
                    setPdfDocument(data);
                })
                .catch(() => {
                    setPdfDocument(null);
                })
                .finally(() => {
                    setLoading(false);
                });
            } else {
                captchaRef.current.setError();
            }
    };

    const disabledDate = (current: Dayjs) => {
        return current.year() < 1900 || (current && current > dayjs().endOf('day'));
    };

    const downloadPdf = async () => {
        let pdf: ArrayBuffer;
        if (pdfDocument) {
            pdf = pdfDocument;
        } else {
            const values = form.getFieldsValue();
            const response = await getDocument(toRequest(values));
            pdf = response.data;
        }
        const filename = 'ResidenceConfirmation.pdf';
        const url = window.URL.createObjectURL(new Blob([pdf]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
    };

    return (
        <>
            <div className="find-taxpayer-service">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                >
                    <Row gutter={16}>
                        <Col
                            xs={24}
                            md={12}
                            lg={5}
                        >
                            <Form.Item
                                label={iinBin}
                                name="iinBin"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Поле обязательно для заполнения',
                                    },
                                    {
                                        min: 12,
                                        message: 'Поле ИИН должно состоять из 12 цифр',
                                    },
                                ]}
                            >
                                <Input
                                    size="large"
                                    placeholder={localize(
                                        'placeholder.input',
                                        props.commonTranslations
                                    )}
                                    onChange={filterDigits(form, 'iinBin')}
                                    maxLength={12}
                                />
                            </Form.Item>
                        </Col>
                        <Col
                            xs={24}
                            md={12}
                            lg={5}
                        >
                            <Form.Item
                                label={regNumber}
                                name="regNumber"
                            >
                                <Input
                                    maxLength={100}
                                    size="large"
                                    placeholder={localize(
                                        'placeholder.input',
                                        props.commonTranslations
                                    )}
                                />
                            </Form.Item>
                        </Col>
                        <Col
                            xs={24}
                            md={8}
                            lg={8}
                        >
                            <Form.Item
                                label={issueDate}
                                name="issueDate"
                                rules={[
                                    {
                                        required: true,
                                        message: localize(
                                            'error.field-is-required',
                                            props.commonTranslations
                                        ),
                                    },
                                ]}
                            >
                                <DatePicker
                                    size="large"
                                    format="DD.MM.YYYY"
                                    placeholder={localize(
                                        'placeholder.issueDate',
                                        props.commonTranslations
                                    )}
                                    disabledDate={disabledDate}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Recaptcha ref={captchaRef} lang={props.lang} />

                    <hr className="my-3" />

                    <Row gutter={{ xs: 8, md: 16 }}>
                        <Col>
                            <Button onClick={clearForm}>
                                {localize('button.reset', props.commonTranslations)}
                            </Button>
                        </Col>
                        <Col>
                            <Button
                                color="gold"
                                htmlType="submit"
                                loading={loading}
                            >
                                {localize('button.find', props.commonTranslations)}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </div>

            {pdfDocument && (
                <div className="taxpayer">
                    <Col
                        xs={24}
                        md={24}
                        lg={24}
                    >
                        <div
                            className="test-questions"
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <span className="test-label">
                                {localize('confirm-residence-kazakhstan', props.translations)}
                            </span>
                            <Col
                                style={{
                                    display: 'flex',
                                }}
                            >
                                <Button
                                    color="gold"
                                    htmlType="submit"
                                    loading={loading}
                                    onClick={downloadPdf}
                                    style={{
                                        marginRight: 10,
                                    }}
                                >
                                    {localize('button.download', props.commonTranslations)}
                                </Button>
                                <PrintForm2
                                    isPost
                                    {...(showOnScan.current ? { isOpen: showOnScan.current } : {})}
                                    apiUrl={
                                        '/services/isnaportalsync/open-api/global/find-document-confirming-residence'
                                    }
                                    params={toRequest(form.getFieldsValue())}
                                    translations={props.commonTranslations}
                                />
                            </Col>
                        </div>
                    </Col>
                </div>
            )}
        </>
    );
};

export default (props) => (
    <PartWrapper {...props}>
        <FindDocumentConfirmingResidence {...props} />
    </PartWrapper>
);
